import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Outlet } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
// ScrollText aliased as Receipt: lucide's Receipt icon contains a "$" glyph — app must stay currency-neutral
import { BarChart3, Upload, Bot, LogOut, Menu, X, BookOpen, ScrollText as Receipt, Coins } from 'lucide-react';
import MainContent from './components/MainContent';
import AIPage from './components/AIPage';
import PricingPage from './components/PricingPage';
import { LoginPage, ProtectedRoute } from './components/auth';
import MultiStepRegistration from './components/auth/MultiStepRegistration';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import UploadedInvoicesPage from './components/UploadedInvoicesPage';
import TemplatePage from './components/template/TemplatePage';
import AddTemplatePage from './components/template/AddTemplatePage';
import TemplateViewer from './components/template/TemplateViewer';
import ContactPage from './components/ContactPage';
import UserGuidePage from './components/UserGuidePage';
import UserMenu from './components/UserMenu';
import LandingPage from './components/LandingPage';
import CreatedInvoicesPage from './components/invoices/CreatedInvoicesPage';
import ExpensesPage from './components/ExpensesPage';
import InvoiceForm from './components/invoices/InvoiceForm';
import InvoicePreview from './components/invoices/InvoicePreview';
import InvoiceSettings from './components/invoices/InvoiceSettings';
import HelpWidget from './components/HelpWidget';
import NotFoundPage from './components/NotFoundPage';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';

// Import the SVG logo
import logo from './assets/logo.svg';
import './styles/shell.css';

const NAV_LINKS = [
  { to: '/dashboard', label: 'Dashboard', icon: <BarChart3 /> },
  { to: '/uploaded-invoices', label: 'Uploaded Invoices', icon: <Upload /> },
  { to: '/created-invoices', label: 'Created Invoices', icon: <Receipt /> },
  { to: '/expenses', label: 'Expenses', icon: <Coins /> },
  { to: '/ai', label: 'AI Assistant', icon: <Bot /> },
  { to: '/guide', label: 'Guide', icon: <BookOpen /> },
];

const Navigation = () => {
  const { logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="vp-nav">
      <div className="vp-nav-inner">
        <div className="vp-nav-row">
          <div className="flex items-center min-w-0">
            <Link to="/dashboard" className="vp-nav-brand">
              <img src={logo} alt="ViewPoint Logo" />
              <span className="font-display">ViewPoint</span>
            </Link>

            {/* Desktop Menu */}
            <div className="vp-nav-links">
              {NAV_LINKS.map(link => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) => `vp-nav-link${isActive ? ' active' : ''}`}
                >
                  {link.icon}
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>

          <div className="vp-nav-actions">
            <ThemeToggle />

            {/* Desktop User Menu */}
            <div className="hidden md:block">
              <UserMenu />
            </div>

            {/* Desktop Logout Button */}
            <button onClick={logout} className="vp-nav-logout">
              <LogOut />
              Logout
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="vp-nav-burger"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="vp-nav-mobile">
            {NAV_LINKS.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `vp-nav-link${isActive ? ' active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.icon}
                {link.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
              className="vp-nav-logout"
            >
              <LogOut />
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            {/* Toasts and confirms wrap the routes so any screen can reach them. */}
            <ToastProvider>
            <ConfirmProvider>
            <div className="flex flex-col min-h-screen bg-gradient-to-br from-[rgb(var(--bg-gradient-from))] via-[rgb(var(--bg-gradient-via))] to-[rgb(var(--bg-gradient-to))] transition-colors duration-300">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<MultiStepRegistration />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/pricing" element={<PricingPage />} />

                {/* Protected Routes */}
                <Route element={
                  <ProtectedRoute>
                    <div className="flex flex-col min-h-screen">
                      <Navigation />
                      <div className="flex-1">
                        <Outlet />
                      </div>
                      <footer className="vp-footer">
                        <p>© {new Date().getFullYear()} ViewPoint. Intelligent document analysis platform.</p>
                      </footer>
                      <HelpWidget />
                    </div>
                  </ProtectedRoute>
                }>
                  <Route path="/dashboard" element={<MainContent />} />
                  <Route path="/ai" element={<AIPage />} />
                  <Route path="/uploaded-invoices">
                    <Route index element={<UploadedInvoicesPage />} />
                    <Route path="templates">
                      <Route index element={<TemplatePage />} />
                      <Route path="new" element={<AddTemplatePage />} />
                      <Route path=":id" element={<TemplateViewer />} />
                    </Route>
                  </Route>
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/created-invoices">
                    <Route index element={<CreatedInvoicesPage />} />
                    <Route path="new" element={<InvoiceForm />} />
                    <Route path="settings" element={<InvoiceSettings />} />
                    <Route path=":id" element={<InvoicePreview />} />
                    <Route path=":id/edit" element={<InvoiceForm />} />
                  </Route>
                  <Route path="/guide" element={<UserGuidePage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  {/* Catch-all. Without it a typo'd URL rendered a blank page
                      under the nav, which reads as a broken app. */}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </div>
            </ConfirmProvider>
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </Provider>
  );
};

export default App;